import { ArrowLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useOrgUsersStore,
  usePremisesStore,
  useRegionStore,
} from "../../../store/store";
import {
  OrgUserType,
  premisesApiResponse,
  WashroomTypeListType,
} from "../../../types/api";
import axiosInstance from "../../../common/axiosInstance";
import { toast } from "react-toastify";
import { DotLoader } from "../../../components/loader/Loader";
// import { WASHROOM_TYPES } from "../../../constants/modules";
import { getAllTypeList } from "../../../service/api";

export const createWashroom = async (washroomData: any) => {
  const response = await axiosInstance.post(
    `/api/washroom`,
    washroomData,
  );
  return response.data;
};

export const updateWashroom = async (id: string, washroomData: any) => {
  const response = await axiosInstance.put(
    `/api/washroom/${id}`,
    washroomData,
  );
  return response.data;
};

const AddWashroom = () => {
  const navigate = useNavigate();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  const location = useLocation();
  const isViewMode = location?.pathname.includes("view-washroom");
  const isUpdateMode = location?.pathname.includes("update-washroom");
  const washroomData = location?.state?.washroom;

  // Form State
  const [selectedWashroomType, setSelectedWashroomType] = useState("");
  const [selectedPremises, setSelectedPremises] = useState("");
  const [selectedAssignedStaff, setSelectedAssignedStaff] = useState("");
  // const [washroomTypes] = useState<string[]>(WASHROOM_TYPES);
  const [washroomName, setWashroomName] = useState("");
  const [floor, setFloor] = useState("");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [filteredPremises, setFilteredPremises] = useState<
    premisesApiResponse[]
  >([]);
  const [filteredStaff, setFilteredStaff] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStaffLoading, setIsStaffLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [floorOptions, setFloorOptions] = useState<string[]>([]);

  const [washroomTypeList, setWashroomTypeList] = useState<
    WashroomTypeListType[]
  >([]);

  const fetchTypes = async () => {
    try {
      setIsLoading(false);
      const res = await getAllTypeList();

      if (res.data?.data && Array.isArray(res.data.data)) {
        setWashroomTypeList(res.data.data);
      } else {
        setWashroomTypeList([]);
      }
    } catch (error) {
      console.error("Failed to fetch washroom types: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  // Initialize data
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([fetchPremises(), fetchOrgUsers(), fetchRegions()]);
      } catch (error) {
        toast.error("Failed to load initial data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [fetchPremises, fetchOrgUsers, fetchRegions]);

  // Initialize form for update/view mode
  useEffect(() => {
    if ((isViewMode || isUpdateMode) && washroomData && regions.length > 0) {
      const region = regions.find((r) => r._id === washroomData.regionId);
      setSelectedRegion(region?._id || "");
      setSelectedWashroomType(washroomData.type || "");
      setSelectedPremises(
        washroomData.premiseId || washroomData.premisesId || "",
      );
      setWashroomName(washroomData.washroomName || "");
      setFloor(washroomData.floor || "");
      if (
        washroomData.assignedStaff &&
        orgUsersData.some(
          (u: OrgUserType) => u._id === washroomData.assignedStaff,
        )
      ) {
        setSelectedAssignedStaff(washroomData.assignedStaff);
      } else {
        setSelectedAssignedStaff("");
      }
    }
  }, [isViewMode, isUpdateMode, washroomData, regions, orgUsersData]);

  // Generate floor options when premises changes
  useEffect(() => {
    if (!selectedPremises) {
      setFloorOptions([]);
      setFloor("");
      return;
    }

    const selectedPremise = allPremises.find((p) => p._id === selectedPremises);
    if (!selectedPremise?.floor) {
      setFloorOptions([]);
      setFloor("");
      return;
    }

    const floorsCount = Number(selectedPremise.floor);
    if (isNaN(floorsCount)) {
      setFloorOptions([]);
      setFloor("");
      return;
    }

    const floors = Array.from(
      { length: floorsCount },
      (_, i) => `Floor ${i + 1}`,
    );
    setFloorOptions(floors);
  }, [selectedPremises, allPremises]);

  // Filter premises based on selected region
  useEffect(() => {
    if (selectedRegion) {
      const premisesInRegion = allPremises.filter(
        (p) => p.region === selectedRegion,
      );
      setFilteredPremises(premisesInRegion);

      if (
        selectedPremises &&
        !premisesInRegion.some((p) => p._id === selectedPremises)
      ) {
        setSelectedPremises("");
      }
    } else {
      setFilteredPremises(allPremises);
    }
  }, [selectedRegion, allPremises, selectedPremises]);

  // Filter staff based on selected premises
  useEffect(() => {
    const filterStaff = async () => {
      setIsStaffLoading(true);

      try {
        if (!selectedPremises || !orgUsersData.length) {
          setFilteredStaff([]);
          return;
        }

        const staffForPremises = orgUsersData.filter((user: any) => {
          const userPremises = Array.isArray(user.premise)
            ? user.premise
            : [user.premise];
          return userPremises.includes(selectedPremises);
        });

        setFilteredStaff(staffForPremises);

        if (
          selectedAssignedStaff &&
          !staffForPremises.some((u: any) => u._id === selectedAssignedStaff)
        ) {
          setSelectedAssignedStaff("");
        }
      } catch (error) {
        toast.error("Failed to filter staff");
      } finally {
        setIsStaffLoading(false);
      }
    };

    filterStaff();
  }, [selectedPremises, orgUsersData, selectedAssignedStaff]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedRegion) newErrors.region = "Region is required";
    if (!selectedPremises) newErrors.premises = "Premises is required";
    if (!washroomName.trim())
      newErrors.washroomName = "Washroom Name/ID is required";
    if (!floor.trim()) newErrors.floor = "Floor/Wing is required";
    if (!selectedWashroomType)
      newErrors.washroomType = "Washroom Type is required";
    if (!selectedAssignedStaff)
      newErrors.assignedStaff = "Assigned Staff is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isViewMode) {
      navigate("/washroom-management/washrooms");
      return;
    }

    if (!validateForm()) {
      const errorMessages = Object.values(errors).join(", ");
      toast.error(`Please fill all required fields: ${errorMessages}`);
      return;
    }

    const payload = {
      premisesId: selectedPremises,
      regionId: selectedRegion,
      washroomName,
      type: selectedWashroomType,
      floor,
      assignedStaff: selectedAssignedStaff,
      createdDate: new Date().toISOString(),
    };

    try {
      if (isUpdateMode && washroomData?._id) {
        await updateWashroom(washroomData._id, payload);
        toast.success("Washroom updated successfully");
      } else {
        await createWashroom(payload);
        toast.success("Washroom created successfully");
      }
      navigate("/washroom-management/washrooms");
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Operation failed");
    }
  };

  const getStaffNameById = (staffId: string) => {
    const staff = orgUsersData.find((u: OrgUserType) => u._id === staffId);
    return staff ? `${staff.firstName} ${staff.lastName}` : "N/A";
  };

  if (isLoading) {
    return <DotLoader />;
  }

  return (
    <div className="p-3 mt-14 ml-2 min-h-100vh">
      <div className="flex justify-between items-center">
        <h2 className="mb-3">
          {isViewMode
            ? "View Washroom"
            : isUpdateMode
              ? "Update Washroom"
              : "Add Washroom"}
        </h2>
        <button
          type="button"
          className="rounded-full flex items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white"
          onClick={() => navigate("/washroom-management/washrooms")}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> BACK
        </button>
      </div>

      <form
        className="bg-white rounded-lg shadow p-4 mx-2 mt-4 grid grid-cols-1 md:grid-cols-2 gap-6"
        id="addWashroom"
        onSubmit={handleSubmit}
      >
        {/* Region */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm mb-1" htmlFor="region">
            Region <span className="text-red-500">*</span>
          </label>
          <select
            id="region"
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            disabled={isViewMode}
            className={`w-full border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 ${
              isViewMode
                ? "bg-gray-100"
                : errors.region
                  ? "border-red-500"
                  : "border-gray-300"
            }`}
          >
            <option value="">Select Region</option>
            {regions.map((region) => (
              <option key={region._id} value={region._id}>
                {region.regionName}
              </option>
            ))}
          </select>
          {errors.region && (
            <p className="text-red-500 text-xs mt-1">{errors.region}</p>
          )}
        </div>

        {/* Premises */}
        <div className="mb-4">
          <label
            className="block text-gray-700 text-sm mb-1"
            htmlFor="premises"
          >
            Premises <span className="text-red-500">*</span>
          </label>
          {isViewMode ? (
            <div className="p-2 bg-gray-100 rounded">
              {allPremises.find((p) => p._id === selectedPremises)
                ?.premisesName || "N/A"}
            </div>
          ) : (
            <select
              id="premises"
              value={selectedPremises}
              onChange={(e) => setSelectedPremises(e.target.value)}
              disabled={isViewMode}
              className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                errors.premises ? "border-red-500" : "border-gray-300"
              }`}
            >
              <option value="">Select Premises</option>
              {filteredPremises.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.premisesName}
                </option>
              ))}
            </select>
          )}
          {errors.premises && (
            <p className="text-red-500 text-xs mt-1">{errors.premises}</p>
          )}
        </div>

        {/* Floor/Wing */}
        <div className="mb-4">
          <label className="block text-gray-700 text-sm mb-1" htmlFor="floor">
            Floor/Wing <span className="text-red-500">*</span>
          </label>
          {isViewMode ? (
            <div className="p-2 bg-gray-100 rounded">{floor || "N/A"}</div>
          ) : (
            <select
              id="floor"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              disabled={isViewMode || !selectedPremises}
              className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                isViewMode || !selectedPremises
                  ? "bg-gray-100"
                  : errors.floor
                    ? "border-red-500"
                    : "border-gray-300"
              }`}
            >
              <option value="">Select Floor</option>
              {floorOptions.map((floorOption) => (
                <option key={floorOption} value={floorOption}>
                  {floorOption}
                </option>
              ))}
            </select>
          )}
          {errors.floor && (
            <p className="text-red-500 text-xs mt-1">{errors.floor}</p>
          )}
        </div>

        {/* Washroom Type */}
        <div className="mb-4">
          <label
            className="block text-gray-700 text-sm mb-1"
            htmlFor="washroomType"
          >
            Washroom Type <span className="text-red-500">*</span>
          </label>
          <select
            id="washroomType"
            value={selectedWashroomType}
            disabled={isViewMode}
            onChange={(e) => setSelectedWashroomType(e.target.value)}
            className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 ${
              isViewMode
                ? "bg-gray-100"
                : errors.washroomType
                  ? "border-red-500"
                  : "border-gray-300"
            }`}
          >
            <option value="">Select Washroom Type</option>
            {washroomTypeList.map((type) => (
              <option key={type._id} value={type._id}>
                {type.typeName}
              </option>
            ))}
            <option value="Others">Others</option>
          </select>
          {errors.washroomType && (
            <p className="text-red-500 text-xs mt-1">{errors.washroomType}</p>
          )}
        </div>

        {/* Assigned Staff */}
        <div className="mb-4">
          <label
            className="block text-gray-700 text-sm mb-1"
            htmlFor="assignedStaff"
          >
            Assigned Staff <span className="text-red-500">*</span>
          </label>
          {isViewMode ? (
            <div className="p-2 bg-gray-100 rounded">
              {getStaffNameById(selectedAssignedStaff)}
            </div>
          ) : (
            <select
              id="assignedStaff"
              value={selectedAssignedStaff}
              onChange={(e) => setSelectedAssignedStaff(e.target.value)}
              disabled={isViewMode || isStaffLoading || !selectedPremises}
              className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 ${
                isViewMode || isStaffLoading || !selectedPremises
                  ? "bg-gray-100"
                  : errors.assignedStaff
                    ? "border-red-500"
                    : "border-gray-300"
              }`}
            >
              <option value="">Select Staff</option>
              {isStaffLoading ? (
                <option value="" disabled>
                  Loading staff...
                </option>
              ) : filteredStaff.length === 0 ? (
                <option value="" disabled>
                  {selectedPremises
                    ? "No staff available for this premises"
                    : "Please select a premises first"}
                </option>
              ) : (
                filteredStaff.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.firstName} {user.lastName}
                  </option>
                ))
              )}
            </select>
          )}
          {errors.assignedStaff && (
            <p className="text-red-500 text-xs mt-1">{errors.assignedStaff}</p>
          )}
        </div>

        <div className="mb-4">
          <label
            className="block text-gray-700 text-sm mb-1"
            htmlFor="washroomName"
          >
            Washroom Name/ID <span className="text-red-500">*</span>
          </label>
          <input
            id="washroomName"
            type="text"
            value={washroomName}
            disabled={isViewMode}
            onChange={(e) => setWashroomName(e.target.value)}
            placeholder="Enter Washroom Name/ID"
            className={`w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-200 ${
              isViewMode
                ? "bg-gray-100"
                : errors.washroomName
                  ? "border-red-500"
                  : "border-gray-300"
            }`}
          />
          {errors.washroomName && (
            <p className="text-red-500 text-xs mt-1">{errors.washroomName}</p>
          )}
        </div>

        {/* Buttons */}
      </form>
      <div className="flex rounded-md justify-end gap-4 p-4 bg-[#5A7192]  sticky bottom-0">
        <button
          type="submit"
          form="addWashroom"
          className="px-6 py-2 rounded-full bg-[#00A8A8] text-white hover:bg-teal-600 transition"
        >
          {isViewMode
            ? "Back"
            : isUpdateMode
              ? "Update Washroom"
              : "Add Washroom"}
        </button>
      </div>
    </div>
  );
};

export default AddWashroom;
